import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Col, FormGroup, Label, FormFeedback } from 'reactstrap';
import { getInputStatus } from '../../../services/commonServices';
import { Types } from '../../../utils/DynamicForm.config';
import PrintInputError from '../../InputUtils/PrintInputError';
import InputDescription from '../../InputUtils/InputDescription';
import clsx from 'clsx';
import useDebounce from '../../../hooks/useDebounce';
import authenticatedFetcher from '../../../services/fetcher';

// Minimum characters before triggering suggestions fetch
const MIN_QUERY_LENGTH = 2;
const SUGGEST_DEBOUNCE_MS = 300;

const TypeFormTextSuggest = (props) => {
	const {
		id,
		label,
		name = '',
		value,
		placeholder,
		onChange,
		required,
		editable = true,
		isEditing = true,
		errors,
		style,
		isSLAField,
		labelColumn = {
			xs: '6',
			md: '2'
		},
		inputColumn = {
			xs: '6',
			md: '10'
		},
		alwaysEditableFields,
		relatedData,
		options = {},
		globalParams = {}
	} = props;

	const [localValue, setLocalValue] = useState(value || '');
	const [suggestions, setSuggestions] = useState([]);
	const debouncedTerm = useDebounce(localValue, SUGGEST_DEBOUNCE_MS);
	const datalistId = `datalist-${name}-${id || 'default'}`;
	const isUserTyping = useRef(false);

	useEffect(() => {
		setLocalValue((prev) => (prev === value ? prev : value));
	}, [value]);

	useEffect(() => {
		onChange({ target: { value: debouncedTerm || '', name } });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedTerm]);

	useEffect(() => {
		if (!isUserTyping.current) return;
		if (!options.endpoint) return;

		if (debouncedTerm && debouncedTerm.length >= MIN_QUERY_LENGTH) {
			fetchSuggestions(debouncedTerm);
		} else {
			setSuggestions([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedTerm]);

	const fetchSuggestions = async (query) => {
		const data = await authenticatedFetcher({
			path: options.endpoint,
			urlParams: {
				q: query,
				...globalParams,
			},
		});
		if (Array.isArray(data)) {
			setSuggestions(data.map((item) => item.value || item));
		} else {
			setSuggestions([]);
		}
	};

	const handleChange = ({ target: { value } }) => {
		isUserTyping.current = true;
		setLocalValue(value);
	};

	const isEditable = Array.isArray(alwaysEditableFields)
		? alwaysEditableFields.some((item) => item === name) || editable
		: editable;

	return (
		<FormGroup tag="fieldset" row>
			{label !== false ? (
				<Col
					{...labelColumn}
					className={`${labelColumn.className} ${isSLAField && 'CustomContractesInfo-sla-text'}`}>
					<Label style={style} for="form-2-last-name">
						{required ? '*' : ''} {label}
					</Label>
				</Col>
			) : (
				''
			)}
			<Col
				{...inputColumn}
				className={`${inputColumn.className} ${isSLAField && 'CustomContractesInfo-sla-text regular'}`}>
				<Form.Control
					id={`form-2-${id}`}
					type="text"
					name={name}
					value={localValue || ''}
					placeholder={placeholder}
					onChange={handleChange}
					disabled={!isEditable || !isEditing}
					className={clsx(!isEditing && 'pl-0', getInputStatus(required, localValue, errors))}
					autoComplete="off"
					list={isEditing && isEditable ? datalistId : undefined}
				/>
				{isEditing && isEditable && suggestions.length > 0 && (
					<datalist id={datalistId}>
						{suggestions.map((suggestion, index) => (
							<option key={`${datalistId}-${index}`} value={suggestion} />
						))}
					</datalist>
				)}
				<FormFeedback>{localValue}</FormFeedback>
				<PrintInputError errors={errors} />
				<InputDescription description={relatedData ? relatedData.description : ''} />
			</Col>
		</FormGroup>
	);
};

TypeFormTextSuggest.propTypes = Types.inputType;

TypeFormTextSuggest.defaultProps = {
	type: 'text'
};

export default TypeFormTextSuggest;
